/**
 * @file 手書きの線の履歴 (追加・取り消し・やり直し・全消し) を持つ hook である。
 *
 * @what: 文書と、取り消した線の山を持つ。線を足すと山は空になる。全消しは消した線を山に積む
 *   (やり直しで戻せる)。変更があったかの印も持つ。
 * @why: メモの手書きとページの上の書き込みで同じ操作が必要なので、状態の扱いを1つにする。
 * @failure: 例外を投げない。
 */

import { useCallback, useState } from "react";
import { emptyHandwriting, type HandwritingDoc, type HandwritingStroke } from "../model/handwriting";

export function useInkHistory(initial?: HandwritingDoc) {
  const [doc, setDoc] = useState<HandwritingDoc>(() => initial ?? emptyHandwriting());
  const [redo, setRedo] = useState<HandwritingStroke[]>([]);
  const [changed, setChanged] = useState(false);

  const push = useCallback((stroke: HandwritingStroke) => {
    setDoc((d) => ({ ...d, strokes: [...d.strokes, stroke] }));
    setRedo([]);
    setChanged(true);
  }, []);

  const undo = useCallback(() => {
    setDoc((d) => {
      const last = d.strokes[d.strokes.length - 1];
      if (!last) return d;
      setRedo((r) => [...r, last]);
      return { ...d, strokes: d.strokes.slice(0, -1) };
    });
    setChanged(true);
  }, []);

  const redoOne = useCallback(() => {
    setRedo((r) => {
      const last = r[r.length - 1];
      if (!last) return r;
      setDoc((d) => ({ ...d, strokes: [...d.strokes, last] }));
      return r.slice(0, -1);
    });
    setChanged(true);
  }, []);

  const clear = useCallback(() => {
    setDoc((d) => {
      if (d.strokes.length === 0) return d;
      setRedo((r) => [...r, ...d.strokes]);
      return { ...d, strokes: [] };
    });
    setChanged(true);
  }, []);

  /** 別の文書に入れ替える (ページを送ったとき)。履歴と変更の印は消す。 */
  const reset = useCallback((next: HandwritingDoc) => {
    setDoc(next);
    setRedo([]);
    setChanged(false);
  }, []);

  const markSaved = useCallback(() => setChanged(false), []);

  return {
    doc,
    changed,
    canUndo: doc.strokes.length > 0,
    canRedo: redo.length > 0,
    push,
    undo,
    redo: redoOne,
    clear,
    reset,
    markSaved,
  };
}
